import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/fr";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Hand, Send, Undo2, User } from "lucide-react";

dayjs.extend(relativeTime);
dayjs.locale("fr");

const BUBBLE_STYLES = {
  client: "self-start bg-card text-card-foreground rounded-2xl rounded-bl-sm",
  agent: "self-end bg-primary text-primary-foreground rounded-2xl rounded-br-sm",
  admin: "self-end bg-[#1E3A8A] text-white rounded-2xl rounded-br-sm",
};

const ROLE_LABELS = { client: "Client", agent: "Agent IA", admin: "Vous (manuel)" };

export const ChatView = ({ phone, onBack, onChanged }) => {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const scrollRef = useRef(null);

  const fetchDetail = useCallback(async () => {
    try {
      const res = await api.get(`/conversations/${phone}`);
      setData(res.data);
    } catch {
      toast.error("Impossible de charger la conversation.");
    }
  }, [phone]);

  useEffect(() => {
    setData(null);
    fetchDetail();
    const interval = setInterval(fetchDetail, 15000);
    return () => clearInterval(interval);
  }, [fetchDetail]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [data?.messages?.length]);

  const conv = data?.conversation;
  const isTakeover = conv?.status === "human_takeover";

  const handleTakeover = async () => {
    setActionLoading(true);
    try {
      await api.post(`/conversations/${phone}/takeover`);
      toast.success("Vous avez repris la main sur cette conversation.");
      await fetchDetail();
      onChanged?.();
    } catch {
      toast.error("Action impossible pour le moment.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRelease = async () => {
    setActionLoading(true);
    try {
      await api.post(`/conversations/${phone}/release`);
      toast.success("L'agent a repris la conversation.");
      await fetchDetail();
      onChanged?.();
    } catch {
      toast.error("Action impossible pour le moment.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      const res = await api.post(`/conversations/${phone}/reply`, { message: reply.trim() });
      setReply("");
      await fetchDetail();
      if (res.data.sent) {
        toast.success("Message envoyé sur WhatsApp.");
      } else {
        toast.warning("Message enregistré, mais l'envoi WhatsApp a échoué (API Meta non configurée ?).");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Envoi impossible.");
    } finally {
      setSending(false);
    }
  };

  if (!data) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
        Chargement de la conversation…
      </div>
    );
  }

  return (
    <motion.div
      key={phone}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="flex-1 flex flex-col h-full"
      data-testid="chat-view"
    >
      {/* En-tête */}
      <div className="flex items-center gap-3 border-b border-border bg-background px-4 py-3">
        <button onClick={onBack} data-testid="chat-back-button" className="md:hidden p-1 text-muted-foreground">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-foreground shrink-0">
          <User className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-serif text-base truncate" data-testid="chat-profile-name">
            {conv.profile_name || "Prospect WhatsApp"}
          </p>
          <p className="text-xs text-muted-foreground">{conv.phone}</p>
        </div>
        <StatusBadge status={conv.status} testId="chat-status-badge" />
        {isTakeover ? (
          <Button
            size="sm"
            variant="outline"
            onClick={handleRelease}
            disabled={actionLoading}
            data-testid="release-button"
            className="rounded-full border-primary text-primary hover:bg-primary hover:text-primary-foreground"
          >
            <Undo2 className="h-4 w-4 mr-1.5" />
            <span className="hidden sm:inline">Rendre à l'agent</span>
            <span className="sm:hidden">Rendre</span>
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={handleTakeover}
            disabled={actionLoading}
            data-testid="takeover-button"
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Hand className="h-4 w-4 mr-1.5" />
            <span className="hidden sm:inline">Reprendre la main</span>
            <span className="sm:hidden">Reprendre</span>
          </Button>
        )}
      </div>

      {/* Résumé de passation */}
      {conv.handoff_summary && (
        <div className="border-b border-border bg-primary/10 px-4 py-2.5 text-xs" data-testid="handoff-summary">
          <p className="font-semibold text-primary mb-0.5">Résumé de passation</p>
          <p className="whitespace-pre-wrap text-foreground/90">{conv.handoff_summary}</p>
        </div>
      )}

      {/* Résumé du lead */}
      {data.lead && (
        <div className="border-b border-border bg-secondary/50 px-4 py-2 text-xs flex flex-wrap gap-x-5 gap-y-1" data-testid="chat-lead-summary">
          <span><strong>Projet :</strong> {data.lead.projet || "—"}</span>
          <span><strong>Secteur :</strong> {data.lead.secteur || "—"}</span>
          <span><strong>Budget :</strong> {data.lead.budget || "—"}</span>
          <span><strong>Délai :</strong> {data.lead.delai || "—"}</span>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="chat-scroll flex-1 overflow-y-auto px-4 py-6 flex flex-col gap-3" data-testid="chat-messages">
        {data.messages.map((msg, idx) => (
          <div key={idx} className={`max-w-[80%] sm:max-w-[65%] px-4 py-2.5 shadow-sm ${BUBBLE_STYLES[msg.role] || BUBBLE_STYLES.client}`}>
            <p className="text-sm whitespace-pre-wrap break-words">{msg.text}</p>
            <p className={`mt-1 text-[10px] ${msg.role === "client" ? "text-muted-foreground" : "opacity-70"}`}>
              {ROLE_LABELS[msg.role] || msg.role} · {dayjs(msg.timestamp).format("HH:mm")}
            </p>
          </div>
        ))}
        {data.messages.length === 0 && (
          <p className="text-center text-sm text-muted-foreground mt-10">Aucun message dans cette conversation.</p>
        )}
      </div>

      {/* Composeur (mode humain) */}
      {isTakeover ? (
        <form onSubmit={handleSend} className="relative z-[10000] border-t border-border bg-background px-4 py-3 flex items-end gap-2">
          <Textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Votre réponse manuelle au prospect…"
            data-testid="reply-input"
            rows={1}
            className="min-h-[44px] max-h-32 resize-none bg-card"
          />
          <Button
            type="submit"
            disabled={sending || !reply.trim()}
            data-testid="reply-send-button"
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 w-11 p-0 shrink-0"
            aria-label="Envoyer"
          >
            <Send className="h-4 w-4" />
          </Button>
        </form>
      ) : (
        <div className="border-t border-border bg-background px-4 py-3 text-center text-xs text-muted-foreground">
          L'agent IA répond automatiquement. Cliquez sur « Reprendre la main » pour répondre vous-même.
        </div>
      )}
    </motion.div>
  );
};
